/**
 * Quotation HTML print template.
 *
 * Renders a print-ready A4 quote using the same `openHtmlForPrint`
 * helper the other reports use (`projectReports.ts`). One source of
 * truth for the print template across web + (future) email PDF
 * attachments — the same HTML can be sent verbatim to a backend
 * Puppeteer worker for server-side PDF gen later.
 *
 * Sections (top → bottom):
 *   1. Letterhead — org logo / name / address / GSTIN
 *   2. Ref panel  — Quote # · Version · Date · Valid until
 *   3. Bill-To    — client name / phone / email / address / GSTIN
 *   4. Project    — optional one-liner referencing the linked project
 *   5. Sections   — one table per "module" (Carpentry / Civil / etc.)
 *   6. Totals     — subtotal → discount → taxable → CGST/SGST or IGST → round-off → grand total
 *   7. In-words   — Indian-numbering rupee phrase
 *   8. Milestones — payment schedule with row totals
 *   9. T&C        — numbered list
 *  10. Bank       — optional account block
 *  11. Signature  — optional inline image + "Authorised Signatory"
 *  12. Footer     — "Generated by InteriorIO" + system date
 *
 * Page-flow CSS keeps tables splittable between rows but never mid-
 * row, repeats the header on each page, and locks the letterhead +
 * totals block as keep-together so the financial summary never
 * splits across pages.
 */

import { formatInr, numberToIndianWords, type Quotation } from '@/lib/quotations';

function escape(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fmtDate(ts: { toMillis(): number } | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts.toMillis()).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function buildQuotationHtml(q: Quotation): string {
  // Pre-compute initials for the logo tile when no logo is set.
  const companyInitials = (q.companyName || 'Studio')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0] ?? '')
    .join('')
    .toUpperCase();

  // ── Sections (one table per module) — clean format matching
  //    the reference: numbered module title + "N items" muted
  //    badge on the right, slim 5-col table, light footer row
  //    for the module subtotal.
  const sectionsHtml = q.sections
    .filter((s) => s.items.length > 0)
    .map((s, sIdx) => {
      const rows = s.items
        .map((it) => {
          const disc = it.discount ?? 0;
          // Compose a "spec line" for items with dimensions: e.g.
          //   "3 nos · 8 ft × 6 ft × 24 in → 144 Sq.ft"
          // Clients see exactly how the qty was derived. Skipped
          // when dimensions are unset. Count + depth only render
          // when they're actually meaningful.
          const dimUnit = it.dimensionUnit ?? 'ft';
          const dimBits: string[] = [];
          if (it.width) dimBits.push(`${it.width} ${dimUnit}`);
          if (it.height) dimBits.push(`${it.height} ${dimUnit}`);
          if (it.depth) dimBits.push(`${it.depth} ${dimUnit}`);
          let specLine = '';
          if (dimBits.length > 0) {
            const countPart =
              it.count && it.count > 1 ? `${it.count} nos · ` : '';
            const qtyPart = `${it.quantity.toFixed(2).replace(/\.00$/, '')} ${escape(it.unit)}`;
            specLine = `<div class="it-spec">${countPart}${dimBits.join(' × ')} → <strong>${qtyPart}</strong></div>`;
          }
          // Supply + Install split — when both rates are set,
          // surface the breakdown under the rate column so the
          // client sees what's material vs labour.
          const showSplit =
            (it.supplyRate ?? 0) > 0 || (it.installRate ?? 0) > 0;
          const rateCell = showSplit
            ? `<div class="num strong">${formatInr(it.rate)}</div>
               <div class="it-split">
                 ${(it.supplyRate ?? 0) > 0 ? `Supply ${formatInr(it.supplyRate!)}` : ''}
                 ${(it.supplyRate ?? 0) > 0 && (it.installRate ?? 0) > 0 ? ' + ' : ''}
                 ${(it.installRate ?? 0) > 0 ? `Install ${formatInr(it.installRate!)}` : ''}
               </div>`
            : `<div class="num strong">${formatInr(it.rate)}</div>`;
          return `
        <tr>
          <td class="it-cell">
            <div class="it-title">${escape(it.name)}</div>
            ${it.description ? `<div class="it-desc">${escape(it.description)}</div>` : ''}
            ${specLine}
            ${
              it.brand || it.hsn
                ? `<div class="it-meta">${
                    it.brand ? `Brand: ${escape(it.brand)}` : ''
                  }${it.brand && it.hsn ? ' · ' : ''}${
                    it.hsn ? `HSN: ${escape(it.hsn)}` : ''
                  }</div>`
                : ''
            }
          </td>
          <td class="num">${it.quantity.toFixed(2).replace(/\.00$/, '')}${it.unit ? `<span class="it-unit"> ${escape(it.unit)}</span>` : ''}</td>
          <td>${rateCell}</td>
          <td class="num">${disc > 0 ? `${disc}%` : '0%'}</td>
          <td class="num strong">${formatInr(it.amount)}</td>
        </tr>`;
        })
        .join('');
      const sectionTotal = s.items.reduce((sum, it) => sum + it.amount, 0);
      const itemCount = s.items.length;
      return `
      <section class="module${sIdx === 0 ? ' first' : ''}">
        <div class="module-head">
          <div class="module-title">${sIdx + 1}. ${escape(s.title || 'Module')}</div>
          <div class="module-count">${itemCount} item${itemCount === 1 ? '' : 's'}</div>
        </div>
        <table class="sec-table">
          <thead>
            <tr>
              <th>Description</th>
              <th class="num">Qty</th>
              <th class="num">Rate</th>
              <th class="num">Disc %</th>
              <th class="num">Amount</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="num sec-total-label">Module subtotal</td>
              <td class="num sec-total-val">${formatInr(sectionTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </section>`;
    })
    .join('');

  // ── Totals (right-column card) — matches the reference's
  //    clean stack: Subtotal / Discount / GST split lines, then
  //    a dark filled "Grand Total" pill at the bottom.
  const totalsRows: string[] = [];
  totalsRows.push(rowTotal('Subtotal', formatInr(q.subtotal)));
  const discLabel =
    q.discountType === 'percent' && q.discountValue > 0
      ? `Discount (${q.discountValue}%)`
      : 'Discount';
  totalsRows.push(
    rowTotal(
      discLabel,
      q.discountAmount > 0
        ? `−${formatInr(q.discountAmount)}`
        : `−${formatInr(0)}`,
    ),
  );
  if (q.gst.enabled && q.gst.rate > 0) {
    if (q.gst.type === 'intra') {
      const half = q.gst.rate / 2;
      totalsRows.push(rowTotal(`CGST (${half}%)`, formatInr(q.cgstAmount)));
      totalsRows.push(rowTotal(`SGST (${half}%)`, formatInr(q.sgstAmount)));
    } else {
      totalsRows.push(rowTotal(`IGST (${q.gst.rate}%)`, formatInr(q.igstAmount)));
    }
  }
  if (q.roundOff && Math.abs(q.roundOff) >= 0.01) {
    totalsRows.push(
      rowTotal('Round-off', `${q.roundOff > 0 ? '+' : '−'}${formatInr(Math.abs(q.roundOff))}`),
    );
  }

  // Payment schedule — single-table list with a soft blue % chip
  // per row. Matches the reference's "Advance on signing | 30% |
  // ₹69,879.60" layout.
  const milestonesHtml = q.paymentMilestones.length
    ? `<div class="ps-list">
         ${q.paymentMilestones
           .map(
             (m) => `
           <div class="ps-row">
             <div class="ps-label">${escape(m.label)}</div>
             <div class="ps-pct">${m.percent.toFixed(0)}%</div>
             <div class="ps-amt">${formatInr(m.amount)}</div>
           </div>`,
           )
           .join('')}
       </div>`
    : '';

  const tncHtml = q.termsAndConditions.length
    ? q.termsAndConditions.length === 1
      ? `<div class="prose">${escape(q.termsAndConditions[0])}</div>`
      : `<ol class="tnc">
           ${q.termsAndConditions.map((t) => `<li>${escape(t)}</li>`).join('')}
         </ol>`
    : '';

  // Notes section — combines free-text notes with optional bank
  // details mention. The reference shows a single inline NOTES
  // block that says "Thank you for considering us. Bank: HDFC
  // A/C ... IFSC ..." so we follow the same pattern.
  const bankSummary =
    q.showBankDetails && q.bankDetails && hasAnyBankField(q.bankDetails)
      ? [
          q.bankDetails.bankName,
          q.bankDetails.accountNumber
            ? `A/C ${q.bankDetails.accountNumber}`
            : null,
          q.bankDetails.ifsc ? `IFSC ${q.bankDetails.ifsc}` : null,
          q.bankDetails.upiId ? `UPI ${q.bankDetails.upiId}` : null,
        ]
          .filter(Boolean)
          .join(', ')
      : '';
  const notesText = [q.notes?.trim(), bankSummary ? `Bank: ${bankSummary}.` : '']
    .filter(Boolean)
    .join(' ');
  const notesHtml = notesText
    ? `<div class="prose">${escape(notesText)}</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escape(q.number)} — ${escape(q.clientName)}</title>
<style>
@page { size: A4; margin: 14mm 14mm; }
* { box-sizing: border-box; }
html { background: #F1F5F9; }
body {
  margin: 0; padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif;
  color: #15171A; font-size: 10pt;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}
/* Screen-only A4-paper container. Print mode strips this so
 * the printed PDF respects @page margins instead of getting a
 * shadowed inner box. */
@media screen {
  html { padding: 28px 0; }
  body {
    max-width: 880px;
    margin: 0 auto;
    background: #fff;
    padding: 44px 48px 40px;
    border-radius: 10px;
    box-shadow:
      0 1px 3px rgba(15,23,42,0.06),
      0 12px 36px rgba(15,23,42,0.08);
  }
}
@media print {
  html { background: #fff; padding: 0; }
  body { max-width: none; margin: 0; padding: 0; box-shadow: none; border-radius: 0; }
}
.num    { text-align: right; font-variant-numeric: tabular-nums; }
.strong { font-weight: 700; }

/* ─── Header (letterhead) ───────────────────────────────── */
.lh {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 28pt;
  padding-bottom: 20pt;
  margin-bottom: 22pt;
  border-bottom: 1px solid #E5E7EB;
  page-break-inside: avoid;
}
.lh-id { display: flex; gap: 14pt; align-items: flex-start; flex: 1; min-width: 0; }
.lh-logo {
  width: 52pt; height: 52pt; flex-shrink: 0;
  border-radius: 9pt;
  background: linear-gradient(135deg, #6366F1, #4338CA);
  color: #fff;
  display: grid; place-items: center;
  font-weight: 800; font-size: 19pt;
  letter-spacing: -0.3px;
  box-shadow: 0 2px 6px rgba(67,56,202,0.25);
  object-fit: cover;
  overflow: hidden;
}
.lh-logo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.lh-name { font-size: 15pt; font-weight: 800; margin: 0 0 4pt 0; color: #0F172A; letter-spacing: -0.2px; line-height: 1.2; }
.lh-meta-line { font-size: 9pt; color: #475569; margin-top: 2pt; line-height: 1.5; }
.lh-meta-line strong { color: #0F172A; font-weight: 500; }
.lh-doc { text-align: right; min-width: 200pt; }
.lh-doc-title { font-size: 22pt; font-weight: 800; color: #0F172A; margin: 0 0 10pt 0; letter-spacing: -0.4px; }
.lh-ref-table { margin-left: auto; }
.lh-ref-table td { padding: 2pt 0 2pt 16pt; font-size: 9.5pt; vertical-align: top; }
.lh-ref-table .lbl { color: #94A3B8; text-align: left; font-weight: 500; padding-left: 0; }
.lh-ref-table .val { font-weight: 700; color: #0F172A; text-align: right; }

/* ─── From / Bill To ────────────────────────────────────── */
.parties {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin-bottom: 26pt;
  border-bottom: 1px solid #E5E7EB;
  padding-bottom: 22pt;
  page-break-inside: avoid;
}
.party { padding-right: 24pt; }
.party + .party {
  padding-right: 0;
  padding-left: 24pt;
  border-left: 1px solid #F1F5F9;
}
.party-tag {
  display: inline-block;
  font-size: 8pt; font-weight: 700;
  color: #4338CA; background: #EEF2FF;
  padding: 2pt 9pt; border-radius: 999pt;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 8pt;
}
.party-name { font-size: 12pt; font-weight: 700; color: #0F172A; margin-bottom: 5pt; }
.party-line { font-size: 9.5pt; color: #475569; line-height: 1.5; }

/* ─── Module sections ───────────────────────────────────── */
.module {
  margin-bottom: 22pt;
  page-break-inside: auto;
}
.module-head {
  display: flex; justify-content: space-between; align-items: baseline;
  padding-bottom: 6pt; margin-bottom: 4pt;
  border-bottom: 1px solid #0F172A;
}
.module-title { font-size: 11pt; font-weight: 700; color: #0F172A; }
.module-count { font-size: 9pt; color: #94A3B8; font-weight: 500; }
.sec-table { width: 100%; border-collapse: collapse; }
.sec-table thead th {
  font-size: 8pt; font-weight: 700;
  color: #94A3B8; letter-spacing: 1px;
  text-transform: uppercase;
  padding: 9pt 10pt;
  text-align: left;
  border-bottom: 1px solid #E5E7EB;
}
.sec-table thead th.num { text-align: right; }
.sec-table tbody td {
  padding: 11pt 10pt;
  border-bottom: 1px solid #F1F5F9;
  font-size: 10pt;
  vertical-align: top;
}
.it-cell { line-height: 1.4; }
.it-title { font-weight: 600; font-size: 10pt; color: #0F172A; }
.it-desc { font-size: 9pt; color: #94A3B8; margin-top: 3pt; line-height: 1.45; }
.it-meta { font-size: 8.5pt; color: #94A3B8; margin-top: 3pt; }
/* Dimension spec line — "3 nos · 8 ft × 6 ft → 144 Sq.ft".
   Shown under the item description so clients can verify
   how the qty was derived. Slightly emphasised so it stands
   out from the regular grey meta line. */
.it-spec {
  font-size: 9pt;
  color: #475569;
  margin-top: 3pt;
  font-variant-numeric: tabular-nums;
}
.it-spec strong { color: #0F172A; }
/* Supply + Install split — rendered under the unit rate in
   the rate column. Clients see "Supply ₹2,000 + Install ₹500"
   so the material vs labour breakdown is transparent. */
.it-split {
  font-size: 8pt;
  color: #94A3B8;
  margin-top: 2pt;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.it-unit { color: #94A3B8; font-weight: 400; font-size: 9pt; }
.sec-table tfoot td {
  padding: 10pt 12pt;
  background: #F8FAFC;
  font-size: 10pt;
  border-bottom: 0;
}
.sec-total-label { color: #475569; font-weight: 500; }
.sec-total-val { font-weight: 700; color: #0F172A; }

/* ─── Bottom split — left (schedule + notes + T&C) | right (totals) ─── */
.bottom {
  display: grid;
  grid-template-columns: 1fr 320pt;
  gap: 28pt;
  border-top: 1px solid #E5E7EB;
  padding-top: 22pt;
  margin-top: 8pt;
  page-break-inside: avoid;
}
.bottom-left { display: flex; flex-direction: column; gap: 18pt; }
.bottom-block { }
.block-title {
  font-size: 8pt; font-weight: 700;
  color: #94A3B8; letter-spacing: 1.2px;
  text-transform: uppercase;
  margin: 0 0 7pt 0;
}
.prose { font-size: 9.5pt; color: #334155; line-height: 1.6; margin: 0; }
.tnc { margin: 0; padding-left: 16pt; }
.tnc li { font-size: 9.5pt; color: #334155; margin-bottom: 4pt; line-height: 1.55; }

/* Payment schedule rows */
.ps-list { display: flex; flex-direction: column; }
.ps-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 14pt;
  align-items: center;
  padding: 9pt 0;
  border-bottom: 1px solid #F1F5F9;
}
.ps-row:last-child { border-bottom: 0; padding-bottom: 0; }
.ps-row:first-child { padding-top: 0; }
.ps-label { font-size: 10pt; color: #0F172A; }
.ps-pct {
  font-size: 9pt; font-weight: 700;
  color: #4338CA; background: #EEF2FF;
  padding: 2pt 10pt; border-radius: 999pt;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ps-amt { font-size: 10pt; font-weight: 700; color: #0F172A; font-variant-numeric: tabular-nums; text-align: right; min-width: 70pt; }

/* Totals (right column) */
.totals { display: flex; flex-direction: column; }
.totals-row {
  display: flex; justify-content: space-between;
  padding: 6pt 0;
  font-size: 10pt;
  color: #475569;
}
.totals-row .val { color: #0F172A; font-weight: 500; font-variant-numeric: tabular-nums; }
.grand-pill {
  margin-top: 10pt;
  background: #0F172A;
  color: #fff;
  border-radius: 10pt;
  padding: 14pt 16pt;
  display: flex; justify-content: space-between; align-items: center;
}
.grand-pill .lbl { font-size: 10pt; color: #CBD5E1; }
.grand-pill .val { font-size: 15pt; font-weight: 800; font-variant-numeric: tabular-nums; letter-spacing: -0.3px; }

/* ─── Footer ────────────────────────────────────────────── */
.foot {
  margin-top: 28pt;
  padding-top: 14pt;
  border-top: 1px solid #E5E7EB;
  display: flex; justify-content: space-between;
  font-size: 8.5pt; color: #94A3B8;
}
.foot .org { color: #475569; }
.foot .org strong { color: #0F172A; font-weight: 600; }

/* Signature stays optional — appears at bottom-left when set. */
.signature {
  margin-top: 18pt;
  display: flex; flex-direction: column; gap: 4pt;
  page-break-inside: avoid;
}
.signature img { max-height: 48pt; max-width: 160pt; }
.sig-line { width: 160pt; border-bottom: 1px solid #94A3B8; margin-bottom: 20pt; }
.sig-label { font-size: 9pt; color: #94A3B8; }
.sig-org { font-size: 10pt; font-weight: 700; color: #0F172A; }

/* Page flow */
table { page-break-inside: auto; }
thead { display: table-header-group; }
tr { page-break-inside: avoid; }
</style>
</head>
<body>
  <header class="lh">
    <div class="lh-id">
      <div class="lh-logo">
        ${q.companyLogoUrl ? `<img src="${escape(q.companyLogoUrl)}" alt="Logo" />` : escape(companyInitials || '·')}
      </div>
      <div style="min-width:0;flex:1;">
        <h1 class="lh-name">${escape(q.companyName || 'Studio')}</h1>
        ${q.companyAddress ? `<div class="lh-meta-line">${escape(q.companyAddress).replace(/\n/g, '<br/>')}</div>` : ''}
        ${q.companyGstin ? `<div class="lh-meta-line">GSTIN: <strong>${escape(q.companyGstin)}</strong></div>` : ''}
        ${
          q.companyEmail || q.companyPhone
            ? `<div class="lh-meta-line">${q.companyEmail ? escape(q.companyEmail) : ''}${q.companyEmail && q.companyPhone ? '  •  ' : ''}${q.companyPhone ? escape(q.companyPhone) : ''}</div>`
            : ''
        }
      </div>
    </div>
    <div class="lh-doc">
      <h2 class="lh-doc-title">Quotation</h2>
      <table class="lh-ref-table">
        <tr><td class="lbl">Quote #</td><td class="val">${escape(q.number)}${q.version > 0 ? ` · R${q.version}` : ''}</td></tr>
        <tr><td class="lbl">Issue Date</td><td class="val">${fmtDate(q.date)}</td></tr>
        <tr><td class="lbl">Valid Until</td><td class="val">${fmtDate(q.validUntil)}</td></tr>
        ${q.projectName ? `<tr><td class="lbl">Reference</td><td class="val">${escape(q.projectName)}</td></tr>` : ''}
      </table>
    </div>
  </header>

  <div class="parties">
    <div class="party">
      <span class="party-tag">From</span>
      <div class="party-name">${escape(q.companyName || 'Studio')}</div>
      ${q.companyAddress ? `<div class="party-line">${escape(q.companyAddress).replace(/\n/g, '<br/>')}</div>` : ''}
      ${q.companyGstin ? `<div class="party-line">GSTIN: ${escape(q.companyGstin)}</div>` : ''}
      ${q.companyEmail ? `<div class="party-line">${escape(q.companyEmail)}</div>` : ''}
    </div>
    <div class="party">
      <span class="party-tag">Bill to</span>
      <div class="party-name">${escape(q.clientName)}</div>
      ${q.clientAddress ? `<div class="party-line">${escape(q.clientAddress).replace(/\n/g, '<br/>')}</div>` : ''}
      ${q.clientGstin ? `<div class="party-line">GSTIN: ${escape(q.clientGstin)}</div>` : ''}
      ${q.clientEmail ? `<div class="party-line">${escape(q.clientEmail)}</div>` : ''}
      ${q.clientPhone ? `<div class="party-line">${escape(q.clientPhone)}</div>` : ''}
    </div>
  </div>

  ${sectionsHtml || '<div style="padding:24pt;text-align:center;color:#94A3B8;font-size:10pt">No items added to this quote.</div>'}

  <div class="bottom">
    <div class="bottom-left">
      ${
        milestonesHtml
          ? `<div class="bottom-block">
               <div class="block-title">Payment schedule</div>
               ${milestonesHtml}
             </div>`
          : ''
      }
      ${
        notesHtml
          ? `<div class="bottom-block">
               <div class="block-title">Notes</div>
               ${notesHtml}
             </div>`
          : ''
      }
      ${
        tncHtml
          ? `<div class="bottom-block">
               <div class="block-title">Terms &amp; conditions</div>
               ${tncHtml}
             </div>`
          : ''
      }
      ${
        q.showSignature
          ? `<div class="signature">
               ${
                 q.companySignatureUrl
                   ? `<img src="${escape(q.companySignatureUrl)}" alt="Signature" />`
                   : '<div class="sig-line"></div>'
               }
               <div class="sig-label">Authorised signatory</div>
               <div class="sig-org">${escape(q.companyName || '')}</div>
             </div>`
          : ''
      }
    </div>
    <div class="totals">
      ${totalsRows.join('')}
      <div class="grand-pill">
        <span class="lbl">Grand Total</span>
        <span class="val">${formatInr(q.total)}</span>
      </div>
      <div style="margin-top:10pt;font-size:8.5pt;color:#94A3B8;line-height:1.5">
        <strong style="color:#475569;font-weight:600;">In words:</strong>
        ${escape(numberToIndianWords(q.total))}
      </div>
    </div>
  </div>

  <div class="foot">
    <div class="org">Generated by <strong>${escape(q.companyName || 'InteriorIO')}</strong></div>
    <div>${escape(fmtDate({ toMillis: () => Date.now() }))}</div>
  </div>
</body>
</html>`;
}

// ───── Tiny row helpers ─────

/** A single label/value row inside the totals column (right side
 *  of the bottom split). Used for Subtotal / Discount / GST split
 *  lines / Round-off. Rendered as a flex row, not a table cell,
 *  so the styling lines up with the dark "Grand Total" pill below
 *  it.  */
function rowTotal(label: string, value: string): string {
  return `<div class="totals-row"><span>${label}</span><span class="val">${value}</span></div>`;
}

function hasAnyBankField(b: { [k: string]: string | undefined }): boolean {
  for (const k of Object.keys(b)) if ((b[k] ?? '').trim()) return true;
  return false;
}
